import React, { useEffect, useMemo, useState } from "react";
import axios from "../../axios";
import TheSpinner from "../TheSpinner";
import { Button, Modal, Table, TableColumnProps, Tag, Tooltip } from "antd";
import { dateDisplayable } from "../table-utils";
import { STUDENT_DEALINGS_STATUS_COLORS, TABLE_LIMIT } from "../constants";
import dayjs from "dayjs";
import TutorCommentsPopup from "../student-drawer/TutorCommentsPopup";
import { useAtom } from "jotai";
import { citysList } from "../../states/citys";

interface TutorHistoryProps {
  tutor_id: string | number;
}

type HistoryRecordType = {
  shortlist_id: number;
  dealings_id: number;
  parent_name: string;
  flat?: string;
  street?: string;
  apartment?: string;
  location: string;
  status: string;
  demo_date: string;
  confirmed_tuition_date: string;
  start_date: string;
  distance: string | number;
  student_requirments: string;
  how_many_days_in_a_week: string | number;
  tuition_start_time: string;
  tuition_hours: string;
  fee_discuss: string;
  comment_count?: string;
  phone?: string;
  gender?: string;
  city_id?: number | null;
  admin_name?: string;
  created_at?: string;
};

type ReportRecordType = Partial<HistoryRecordType> & {
  dealings_id: number;
  parent_name?: string;
  location?: string;
  status?: string;
  distance?: string | number;
  student_requirments?: string;
  applied_at?: string;
  is_hotlead?: boolean;
  phone?: string;
  gender?: string;
  city_id?: number | null;
  admin_name?: string;
  created_at?: string;
};

function openDealPage(dealingsId: number | string) {
  window.open(`/student/${dealingsId}`, "_blank", "noopener,noreferrer");
}

type ReportMetricKey =
  | "recommended"
  | "applied"
  | "total"
  | "confirmed"
  | "cancelled"
  | "rejected";

const REPORT_METRIC_LABELS: Record<ReportMetricKey, string> = {
  recommended: "Recommended Classes",
  applied: "Applied Classes",
  total: "Requirements Assigned",
  confirmed: "Tuitions Confirmed",
  cancelled: "Tuitions Cancelled",
  rejected: "Tuitions Rejected",
};

const TutorHistory: React.FC<TutorHistoryProps> = (props: TutorHistoryProps) => {
  const { tutor_id } = props;
  const [citys] = useAtom(citysList);
  const [history, setHistory] = useState<HistoryRecordType[]>([]);
  const [recommendedRecords, setRecommendedRecords] = useState<ReportRecordType[]>([]);
  const [appliedRecords, setAppliedRecords] = useState<ReportRecordType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [report, setReport] = useState<
    Partial<{
      assigned: number;
      cancelled: number;
      confirmed: number;
      rejected: number;
      recommended: number;
      applied: number;
      total: number;
    }>
  >({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalRecords, setModalRecords] = useState<ReportRecordType[]>([]);

  const reportModalColumns: TableColumnProps<ReportRecordType>[] = useMemo(
    () => [
      {
        title: "Deal ID",
        dataIndex: "dealings_id",
        key: "dealings_id",
        width: 90,
        fixed: "left",
        render: (dealingsId: number) => (
          <Button type="link" size="small" onClick={() => openDealPage(dealingsId)} style={{ padding: 0 }}>
            {dealingsId}
          </Button>
        ),
      },
      {
        title: "Created At",
        dataIndex: "created_at",
        key: "created_at",
        width: 110,
        render: dateDisplayable,
      },
      {
        title: "Parent Name",
        dataIndex: "parent_name",
        key: "parent_name",
        width: 140,
        ellipsis: true,
      },
      {
        title: "Phone",
        dataIndex: "phone",
        key: "phone",
        width: 120,
      },
      {
        title: "Gender",
        dataIndex: "gender",
        key: "gender",
        width: 80,
      },
      {
        title: "Assigned To",
        dataIndex: "admin_name",
        key: "admin_name",
        width: 140,
        ellipsis: true,
      },
      {
        title: "City",
        dataIndex: "city_id",
        key: "city_id",
        width: 100,
        render: (_: unknown, record: ReportRecordType) => {
          const cityName = record.city_id ? (citys as Record<number, string>)[record.city_id] : "";
          return cityName || "-";
        },
      },
      {
        title: "Parent Address",
        dataIndex: "location",
        key: "location",
        width: 160,
        ellipsis: true,
        render: (location: string) => {
          const address = location
            ?.split("<-->")
            .map((area) => (area !== "undefined" ? area : ""))
            .join(" ")
            .trim();
          return (
            <Tooltip placement="topLeft" title={address || location}>
              <span>{address || location}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 110,
        render: (status: string) => (
          <Tag color={STUDENT_DEALINGS_STATUS_COLORS[status as keyof typeof STUDENT_DEALINGS_STATUS_COLORS]}>
            {status}
          </Tag>
        ),
      },
      {
        title: "Distance",
        dataIndex: "distance",
        key: "distance",
        width: 70,
      },
      {
        title: "Subjects",
        dataIndex: "student_requirments",
        key: "student_requirments",
        width: 180,
        ellipsis: true,
      },
    ],
    [citys],
  );

  const _columns: TableColumnProps<HistoryRecordType>[] = [
    {
      title: "Deal ID",
      dataIndex: "dealings_id",
      key: "dealings_id",
      width: 70,
    },
    {
      title: "Parent Name",
      dataIndex: "parent_name",
      key: "parent_name",
      width: 150,
      render: (_: any, record) => {
        return (
          <Tooltip placement="topLeft" title={record?.parent_name}>
            <div
              style={{
                padding: "5px",
              }}
            >
              {record?.parent_name}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Parent Address",
      dataIndex: "location",
      key: "location",
      width: 180,
      render: (_, record) => {
        const address = record?.location
          ?.split("<-->")
          .map((area) => {
            if (area !== "undefined") return area;
            return "";
          })
          .join(" ");
        return (
          <Tooltip placement="topLeft" title={address}>
            <div
              style={{
                padding: "5px",
              }}
            >
              {address}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (_: any, { status }: { status: string }) => (
        <Tag color={STUDENT_DEALINGS_STATUS_COLORS[status as keyof typeof STUDENT_DEALINGS_STATUS_COLORS]}>
          {status}
        </Tag>
      ),
    },
    {
      title: "Demo Date & Timing",
      dataIndex: "demo_date",
      key: "demo_date",
      width: 100,
      render: dateDisplayable,
    },
    {
      title: "Confirmed Tuition Date",
      dataIndex: "confirmed_tuition_date",
      key: "confirmed_tuition_date",
      width: 100,
      render: dateDisplayable,
    },
    {
      title: "Start Date & Timing",
      dataIndex: "start_date",
      key: "start_date",
      width: 100,
      render: dateDisplayable,
    },
    {
      title: "Distance",
      dataIndex: "distance",
      key: "distance",
      width: 70,
    },
    {
      title: "Subjects",
      dataIndex: "student_requirments",
      key: "student_requirments",
      width: 200,
      ellipsis: true,
      render: (_: any, record) => {
        return (
          <Tooltip placement="topLeft" title={record.student_requirments}>
            <div
              style={{
                padding: "5px",
              }}
            >
              {record.student_requirments}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "No of Classes/Week",
      dataIndex: "how_many_days_in_a_week",
      key: "how_many_days_in_a_week",
      width: 100,
    },
    {
      title: "Tuition Timing",
      dataIndex: "tuition_start_time",
      key: "tuition_start_time",
      width: 100,
      render: (_: any, record) => {
        return <>{record.tuition_start_time ? dayjs(record.tuition_start_time, "HH:mm:ss").format("hh:mm A") : ""}</>;
      },
    },
    {
      title: "No of Teaching Hours",
      dataIndex: "tuition_hours",
      key: "tuition_hours",
      width: 100,
    },
    {
      title: "Fee",
      dataIndex: "fee_discuss",
      key: "fee_discuss",
      width: 100,
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      width: 200,
      align: "center",
      render: (cellValue: string, record) => (
        <TutorCommentsPopup
          cellValue={cellValue}
          data={{ shortlist_id: record.shortlist_id, comment_count: record.comment_count || 0 }}
        />
      ),
    },
  ];

  useEffect(() => {
    fetchHistory();
  }, []);

  async function fetchHistory() {
    try {
      const { data } = await axios.get(`/v1/cre/tutor/history`, {
        params: {
          tutor_id: tutor_id,
        },
      });
      setHistory(data.data?.data ?? []);
      setReport(data.data?.report);
      setRecommendedRecords(data.data?.recommended_records ?? []);
      setAppliedRecords(data.data?.applied_records ?? []);
    } catch (err) {
      console.log("Error is ", err);
    } finally {
      setLoading(false);
    }
  }

  function getRecordsForMetric(metric: ReportMetricKey): ReportRecordType[] {
    switch (metric) {
      case "recommended":
        return recommendedRecords;
      case "applied":
        return appliedRecords;
      case "total":
        return history;
      case "confirmed":
        return history.filter((row) => row.status === "CONFIRMED");
      case "cancelled":
        return history.filter((row) => row.status === "CANCELLED");
      case "rejected":
        return history.filter((row) => row.status === "REJECTED");
      default:
        return [];
    }
  }

  function openReportModal(metric: ReportMetricKey) {
    const count = report?.[metric] ?? 0;
    if (!count) return;

    const records = getRecordsForMetric(metric);
    setModalTitle(REPORT_METRIC_LABELS[metric]);
    setModalRecords(records);
    setModalOpen(true);
  }

  function closeReportModal() {
    setModalOpen(false);
    setModalRecords([]);
    setModalTitle("");
  }

  function renderReportMetric(label: string, metric: ReportMetricKey, count?: number) {
    const value = count ?? 0;
    const isClickable = value > 0;

    return (
      <div className="tail-text-lg tail-font-semibold tail-mb-4">
        {label}:{" "}
        {isClickable ? (
          <button
            type="button"
            onClick={() => openReportModal(metric)}
            className="tail-text-blue-600 tail-underline tail-cursor-pointer tail-bg-transparent tail-border-0 tail-p-0 tail-font-semibold hover:tail-text-blue-800"
          >
            {value}
          </button>
        ) : (
          <span>{value}</span>
        )}
      </div>
    );
  }

  const tutorRating = Math.round(((report?.confirmed || 0) * 100) / (report?.total || 1));

  if (loading) {
    return <TheSpinner />;
  }

  return (
    <div className="tail-mt-4">
      <section className="tail-p-4 tail-bg-white tail-rounded-md tail-shadow-sm">
        <div className="tail-grid tail-grid-cols-1 sm:tail-grid-cols-2 md:tail-grid-cols-3 tail-gap-x-8 tail-gap-y-4">
          {renderReportMetric("Recommended Classes", "recommended", report?.recommended)}
          {renderReportMetric("Applied Classes", "applied", report?.applied)}
          {renderReportMetric("Requirements Assigned", "total", report?.total)}
          {renderReportMetric("Tuitions Confirmed", "confirmed", report?.confirmed)}
          {renderReportMetric("Tuitions Cancelled", "cancelled", report?.cancelled)}
          {renderReportMetric("Tuitions Rejected", "rejected", report?.rejected)}
          <div className="tail-text-lg tail-font-semibold tail-mb-4">Tutor Rating: {tutorRating}%</div>
        </div>
      </section>

      <Modal
        title={modalTitle}
        open={modalOpen}
        onCancel={closeReportModal}
        footer={null}
        width={1200}
        destroyOnClose
      >
        <Table
          dataSource={modalRecords}
          columns={reportModalColumns}
          rowKey={(record, index) =>
            `${record.dealings_id}-${record.shortlist_id ?? index}`
          }
          bordered
          pagination={{ pageSize: TABLE_LIMIT }}
          scroll={{ x: "100%" }}
          size="small"
        />
      </Modal>

      <Table
        dataSource={history}
        bordered
        pagination={{ pageSize: TABLE_LIMIT }}
        scroll={{ x: "100%" }}
        tableLayout="fixed"
        columns={_columns}
        rowKey="shortlist_id"
      />
    </div>
  );
};

export default TutorHistory;
